/**
 * Agent Agreement Component
 * Pre-loaded agreement with editable party info and signature sections
 */
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { openAuthenticatedFile } from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { SignaturePad } from '@/components/common/SignaturePad';
import { 
  FileText, Download, Upload, Eye, Edit2, Save, 
  CheckCircle, Lock, AlertCircle, Loader2, FileSignature, ShieldCheck
} from 'lucide-react';

// Agreement URLs
const AGREEMENT_TEMPLATE_URL = "https://customer-assets.emergentagent.com/job_fd0d82d9-3072-4ce5-8948-087ee83c5afd/artifacts/05n6uudl_NON-EXCLUSIVE-ASSOCIATE-PARTNER-AGENT-AGREEMENT.pdf";
const WATERMARKED_AGREEMENT_URL = `${process.env.REACT_APP_BACKEND_URL}/api/agents/agreement/download-watermarked`;
const SIGNED_AGREEMENT_URL = `${process.env.REACT_APP_BACKEND_URL}/api/agents/agreement/signed-pdf`;
const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const AgentAgreement = ({ 
  agentId, 
  agentName,
  agentEmail,
  agentPhone,
  agentAddress,
  agreementData,
  onSave,
  onUpload,
  isAdmin = false,
  readOnly = false
}) => {
  const [showAgreementDialog, setShowAgreementDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Editable fields - Agent Party Information
  const [agentInfo, setAgentInfo] = useState({
    businessName: agreementData?.agent_info?.businessName || '',
    businessAddress: agreementData?.agent_info?.businessAddress || '',
    directorName: agreementData?.agent_info?.directorName || agentName || '',
    directorAddress: agreementData?.agent_info?.directorAddress || agentAddress || '',
    phone: agreementData?.agent_info?.phone || agentPhone || '',
    email: agreementData?.agent_info?.email || agentEmail || ''
  });
  
  // Editable fields - Agent Signature
  const [agentSignature, setAgentSignature] = useState({
    signedName: agreementData?.signature?.signedName || '',
    signedCompany: agreementData?.signature?.signedCompany || '',
    signedDate: agreementData?.signature?.signedDate || ''
  });

  // E-signature flow state
  const [agentSig, setAgentSig] = useState({ signature_type: 'typed', signature_value: agentName || '' });
  const [companySig, setCompanySig] = useState({ signature_type: 'typed', signature_value: '' });
  const [companySignerName, setCompanySignerName] = useState('');
  const [submittingSign, setSubmittingSign] = useState(false);

  // Agreement status flags
  const agentSignedAt = agreementData?.agent_signed_at;
  const companySignedAt = agreementData?.company_signed_at;
  const fullySigned = !!(agentSignedAt && companySignedAt);

  // Agreement status
  const agreementStatus = agreementData?.status || 'pending';
  const uploadedFile = agreementData?.uploaded_file_url;

  useEffect(() => {
    // Pre-fill from agent details if available
    if (!agreementData?.agent_info?.businessName) {
      setAgentInfo(prev => ({
        ...prev,
        directorName: agentName || prev.directorName,
        phone: agentPhone || prev.phone,
        email: agentEmail || prev.email,
        directorAddress: agentAddress || prev.directorAddress
      }));
    }
  }, [agentName, agentEmail, agentPhone, agentAddress]);

  const handleSave = async () => {
    if (!agentInfo.businessName || !agentInfo.directorName) {
      toast.error('Please fill in at least Business Name and Director Name');
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave({
        agent_info: agentInfo,
        signature: agentSignature,
        status: agentSignature.signedName && agentSignature.signedDate ? 'signed' : 'draft'
      });
      toast.success('Agreement saved successfully');
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save agreement');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitSignature = async () => {
    if (!agentSig.signature_value || (agentSig.signature_type === 'drawn' && !agentSig.signature_value.startsWith('data:'))) {
      toast.error('Please add your signature first');
      return;
    }
    setSubmittingSign(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/agents/agreement/sign`,
        {
          signature_type: agentSig.signature_type,
          signature_value: agentSig.signature_value,
          signer_name: agentName || agentInfo.directorName || agentSig.signature_value,
        },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } },
      );
      if (data?.fully_signed) {
        toast.success('Agreement is now fully signed by both parties. Email sent to both sides.');
      } else {
        toast.success('Your signature has been recorded. Awaiting countersignature from Higher Education Direct.');
      }
      onSave?.({ ...agreementData, agent_signed_at: data?.signed_at, status: data?.status });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to record signature'));
    } finally {
      setSubmittingSign(false);
    }
  };

  const handleSubmitCompanySignature = async () => {
    if (!companySig.signature_value || !companySignerName.trim()) {
      toast.error('Please enter your name and provide a signature');
      return;
    }
    setSubmittingSign(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/agents/${agentId}/agreement/sign-as-company`,
        {
          signature_type: companySig.signature_type,
          signature_value: companySig.signature_value,
          signer_name: companySignerName.trim(),
        },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } },
      );
      if (data?.fully_signed) {
        toast.success('Agreement is now fully signed. Email sent to both parties.');
      } else {
        toast.success('Company signature recorded. Awaiting agent signature.');
      }
      onSave?.({ ...agreementData, company_signed_at: data?.signed_at, status: data?.status });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to sign as company'));
    } finally {
      setSubmittingSign(false);
    }
  };

  const downloadSignedPdf = async () => {
    try {
      const url = isAdmin
        ? `${SIGNED_AGREEMENT_URL}?agent_id=${encodeURIComponent(agentId)}`
        : SIGNED_AGREEMENT_URL;
      await openAuthenticatedFile(url, `Signed-Agreement-${agentName || 'agent'}.pdf`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not download the signed PDF'));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploadingFile(true);
    try {
      await onUpload(file);
      toast.success('Signed agreement uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload agreement');
    } finally {
      setUploadingFile(false);
    }
  };

  const getStatusBadge = () => {
    switch (agreementStatus) {
      case 'signed':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Signed</Badge>;
      case 'draft':
        return <Badge className="bg-yellow-100 text-yellow-700"><Edit2 className="w-3 h-3 mr-1" /> Draft</Badge>;
      case 'uploaded':
        return <Badge className="bg-blue-100 text-blue-700"><Upload className="w-3 h-3 mr-1" /> Uploaded</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700"><AlertCircle className="w-3 h-3 mr-1" /> Pending</Badge>;
    }
  };

  return (
    <>
      {/* Agreement Card Preview */}
      <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-slate-800 dark:to-slate-700 border-2 border-indigo-200 dark:border-indigo-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center justify-between">
            <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
              <FileSignature className="w-5 h-5" />
              Partner Agreement
            </div>
            {getStatusBadge()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Non-Exclusive Associate Partner (Agent) Agreement
          </p>
          
          <div className="flex flex-wrap gap-2">
            {/* View/Edit Agreement */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowAgreementDialog(true)}
              className="flex items-center gap-1"
              data-testid="view-agreement-btn"
            >
              <Eye className="w-4 h-4" />
              {readOnly ? 'View' : 'View & Edit'}
            </Button>
            
            {/* Download Watermarked PDF */}
            <Button
              size="sm"
              variant="outline"
              className="flex items-center gap-1"
              onClick={() => {
                const token = localStorage.getItem('token');
                window.open(`${WATERMARKED_AGREEMENT_URL}?token=${token}`, '_blank');
              }}
              data-testid="download-agreement-btn"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </Button>
            
            {/* Upload Signed Version */}
            {!readOnly && (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="upload-agreement-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1 pointer-events-none"
                  disabled={uploadingFile}
                >
                  {uploadingFile ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Upload Signed
                </Button>
              </label>
            )}
          </div>
          
          {/* Show uploaded file if exists */}
          {uploadedFile && (
            <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700 dark:text-green-300">Signed agreement uploaded</span>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await openAuthenticatedFile(uploadedFile);
                    } catch (e) {
                      const msg = e?.message || 'Unable to open the signed agreement.';
                      toast.error(msg);
                    }
                  }}
                  className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                  data-testid="view-signed-agreement-btn"
                >
                  <Eye className="w-3 h-3" /> View
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Agreement Dialog */}
      <Dialog open={showAgreementDialog} onOpenChange={setShowAgreementDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-indigo-600" />
                Non-Exclusive Associate Partner (Agent) Agreement
              </span>
              {!readOnly && (
                <Button
                  size="sm"
                  variant={isEditing ? "default" : "outline"}
                  onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                  disabled={isSaving}
                  data-testid="edit-save-agreement-btn"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : isEditing ? (
                    <Save className="w-4 h-4 mr-1" />
                  ) : (
                    <Edit2 className="w-4 h-4 mr-1" />
                  )}
                  {isEditing ? 'Save Changes' : 'Edit Details'}
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Company Information (LOCKED) */}
            <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-lg border-2 border-gray-200 dark:border-slate-600">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-4 h-4 text-gray-500" />
                <h3 className="font-semibold text-gray-800 dark:text-white">The Company (Locked)</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-gray-500">Company Name</Label>
                  <p className="font-medium">Higher Education Direct Consultants (SMC-Private) Limited</p>
                </div>
                <div>
                  <Label className="text-gray-500">Designation</Label>
                  <p className="font-medium">"the Company" or "Higher Education Direct"</p>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-gray-500">Address</Label>
                  <p className="font-medium">Rafey's Height, 2nd Floor, Flat 306, Abdara Road, Near MM Academy, Main University Road, Peshawar</p>
                </div>
              </div>
            </div>

            {/* Agent Information (EDITABLE) */}
            <div className={`p-4 rounded-lg border-2 ${isEditing ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600'}`}>
              <div className="flex items-center gap-2 mb-3">
                {isEditing ? (
                  <Edit2 className="w-4 h-4 text-indigo-600" />
                ) : (
                  <FileText className="w-4 h-4 text-gray-500" />
                )}
                <h3 className="font-semibold text-gray-800 dark:text-white">The Agent (Editable)</h3>
                {isEditing && <Badge className="bg-indigo-100 text-indigo-700">Editing</Badge>}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600 dark:text-gray-300">Business Name *</Label>
                  {isEditing ? (
                    <Input
                      value={agentInfo.businessName}
                      onChange={(e) => setAgentInfo({...agentInfo, businessName: e.target.value})}
                      placeholder="Your business/company name"
                      className="mt-1"
                      data-testid="agent-business-name"
                    />
                  ) : (
                    <p className="font-medium mt-1">{agentInfo.businessName || <span className="text-gray-400 italic">Not filled</span>}</p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-600 dark:text-gray-300">Director Name *</Label>
                  {isEditing ? (
                    <Input
                      value={agentInfo.directorName}
                      onChange={(e) => setAgentInfo({...agentInfo, directorName: e.target.value})}
                      placeholder="Director's full name"
                      className="mt-1"
                      data-testid="agent-director-name"
                    />
                  ) : (
                    <p className="font-medium mt-1">{agentInfo.directorName || <span className="text-gray-400 italic">Not filled</span>}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Label className="text-gray-600 dark:text-gray-300">Business Address</Label>
                  {isEditing ? (
                    <Textarea
                      value={agentInfo.businessAddress}
                      onChange={(e) => setAgentInfo({...agentInfo, businessAddress: e.target.value})}
                      placeholder="Full business address"
                      className="mt-1"
                      rows={2}
                    />
                  ) : (
                    <p className="font-medium mt-1">{agentInfo.businessAddress || <span className="text-gray-400 italic">Not filled</span>}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Label className="text-gray-600 dark:text-gray-300">Director Address</Label>
                  {isEditing ? (
                    <Textarea
                      value={agentInfo.directorAddress}
                      onChange={(e) => setAgentInfo({...agentInfo, directorAddress: e.target.value})}
                      placeholder="Director's residential address"
                      className="mt-1"
                      rows={2}
                    />
                  ) : (
                    <p className="font-medium mt-1">{agentInfo.directorAddress || <span className="text-gray-400 italic">Not filled</span>}</p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-600 dark:text-gray-300">Phone</Label>
                  {isEditing ? (
                    <Input
                      value={agentInfo.phone}
                      onChange={(e) => setAgentInfo({...agentInfo, phone: e.target.value})}
                      placeholder="Phone number"
                      className="mt-1"
                    />
                  ) : (
                    <p className="font-medium mt-1">{agentInfo.phone || <span className="text-gray-400 italic">Not filled</span>}</p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-600 dark:text-gray-300">Email</Label>
                  {isEditing ? (
                    <Input
                      value={agentInfo.email}
                      onChange={(e) => setAgentInfo({...agentInfo, email: e.target.value})}
                      placeholder="Email address"
                      className="mt-1"
                    />
                  ) : (
                    <p className="font-medium mt-1">{agentInfo.email || <span className="text-gray-400 italic">Not filled</span>}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Agreement Clauses (LOCKED) */}
            <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-lg border-2 border-gray-200 dark:border-slate-600">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-4 h-4 text-gray-500" />
                <h3 className="font-semibold text-gray-800 dark:text-white">Agreement Terms (Locked)</h3>
              </div>
              
              <div className="space-y-4 text-sm">
                <div className="p-3 bg-white dark:bg-slate-800 rounded border">
                  <h4 className="font-semibold text-teal-700 dark:text-teal-400 mb-2">Appointment & Status</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300">
                    <li>Non-exclusive basis to identify and refer prospective students</li>
                    <li>No partnership, joint venture, employment, or fiduciary relationship created</li>
                    <li>No authority to bind, represent, or make commitments on behalf of the Company</li>
                  </ul>
                </div>
                
                <div className="p-3 bg-white dark:bg-slate-800 rounded border">
                  <h4 className="font-semibold text-teal-700 dark:text-teal-400 mb-2">Scope of Services</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300">
                    <li>Promote only programmes officially approved by the Company</li>
                    <li>Submit all applications solely through the Company's CRM system</li>
                    <li>No offline, parallel, or direct submissions permitted</li>
                  </ul>
                </div>
                
                <div className="p-3 bg-white dark:bg-slate-800 rounded border">
                  <h4 className="font-semibold text-teal-700 dark:text-teal-400 mb-2">CRM, Data & Commission</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300">
                    <li>All data must be entered and maintained within the CRM</li>
                    <li>Commission valid only when approved by authorized administrators</li>
                    <li>Data outside the CRM has no legal standing</li>
                  </ul>
                </div>
                
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                  <h4 className="font-semibold text-red-700 dark:text-red-400 mb-2">Ghost/Non-Genuine Student Penalty</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Agent shall be liable to pay <strong>GBP 1,500</strong> for each non-genuine, fraudulent, or ghost student submission.
                  </p>
                </div>
                
                <div className="p-3 bg-white dark:bg-slate-800 rounded border">
                  <h4 className="font-semibold text-teal-700 dark:text-teal-400 mb-2">Key Legal Provisions</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300">
                    <li>Confidentiality & Non-Circumvention obligations</li>
                    <li>GDPR Compliance (Company as data controller, Agent as processor)</li>
                    <li>Governed by laws of Pakistan</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 text-center">
                <a 
                  href={AGREEMENT_TEMPLATE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline text-sm flex items-center justify-center gap-1"
                >
                  <FileText className="w-4 h-4" />
                  View Full Agreement PDF
                </a>
              </div>
            </div>

            {/* Signature Section (EDITABLE) */}
            <div className={`p-4 rounded-lg border-2 ${isEditing ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600'}`}>
              <div className="flex items-center gap-2 mb-4">
                {isEditing ? (
                  <Edit2 className="w-4 h-4 text-indigo-600" />
                ) : (
                  <FileSignature className="w-4 h-4 text-gray-500" />
                )}
                <h3 className="font-semibold text-gray-800 dark:text-white">Signature Section (Editable)</h3>
                {isEditing && <Badge className="bg-indigo-100 text-indigo-700">Editing</Badge>}
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Company Signature — interactive for authorised signers,
                    read-only otherwise */}
                <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border-2 border-gray-200 dark:border-slate-600">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-700 dark:text-gray-200">For Higher Education Direct</h4>
                    {companySignedAt ? (
                      <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white">
                        <CheckCircle className="w-3 h-3 mr-1" /> Signed
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-700 border-amber-300">Pending</Badge>
                    )}
                  </div>

                  {companySignedAt ? (
                    <div className="space-y-1 text-sm">
                      <div><Label className="text-gray-500">Name</Label><p className="font-medium">{agreementData?.company_signer_name || 'Higher Education Direct'}</p></div>
                      <div><Label className="text-gray-500">Title</Label><p className="font-medium">{agreementData?.company_signer_title || 'Authorised Signatory'}</p></div>
                      <div><Label className="text-gray-500">Signed at</Label><p className="font-medium">{(companySignedAt || '').replace('T', ' ').slice(0, 19)} UTC</p></div>
                      {agreementData?.company_signature_type === 'typed' && (
                        <div className="mt-2 p-2 border-2 border-dashed border-emerald-200 rounded text-center bg-emerald-50 dark:bg-emerald-950/30">
                          <span className="italic text-2xl text-emerald-800 dark:text-emerald-200" style={{ fontFamily: 'cursive, "Brush Script MT"' }}>
                            {agreementData?.company_signature_value || agreementData?.company_signer_name}
                          </span>
                        </div>
                      )}
                      {agreementData?.company_signature_type === 'drawn' && agreementData?.company_signature_value && (
                        <img src={agreementData.company_signature_value} alt="Company signature" className="mt-2 max-h-20 mx-auto" />
                      )}
                    </div>
                  ) : isAdmin ? (
                    <div className="space-y-3" data-testid="company-sign-block">
                      <div>
                        <Label className="text-gray-600 dark:text-gray-300">Your Name *</Label>
                        <Input
                          value={companySignerName}
                          onChange={(e) => setCompanySignerName(e.target.value)}
                          placeholder="e.g. Mr Ibn E Amin"
                          className="mt-1"
                          data-testid="company-signer-name"
                        />
                      </div>
                      <SignaturePad value={companySig} onChange={setCompanySig} testIdPrefix="company-sig" />
                      <Button
                        onClick={handleSubmitCompanySignature}
                        disabled={submittingSign}
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                        data-testid="submit-company-signature"
                      >
                        {submittingSign ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                        Sign on behalf of HE Direct
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic mt-2">
                      Awaiting countersignature from Higher Education Direct.
                      You will receive an email as soon as the agreement is countersigned.
                    </p>
                  )}
                </div>
                
                {/* Agent Signature — interactive for the agent themselves */}
                <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border-2 border-gray-200 dark:border-slate-600">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-700 dark:text-gray-200">For the Agent</h4>
                    {agentSignedAt ? (
                      <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white">
                        <CheckCircle className="w-3 h-3 mr-1" /> Signed
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-700 border-amber-300">Pending</Badge>
                    )}
                  </div>

                  {agentSignedAt ? (
                    <div className="space-y-1 text-sm">
                      <div><Label className="text-gray-500">Name</Label><p className="font-medium">{agreementData?.agent_signer_name || agentName}</p></div>
                      <div><Label className="text-gray-500">Signed at</Label><p className="font-medium">{(agentSignedAt || '').replace('T', ' ').slice(0, 19)} UTC</p></div>
                      {agreementData?.agent_signature_type === 'typed' && (
                        <div className="mt-2 p-2 border-2 border-dashed border-emerald-200 rounded text-center bg-emerald-50 dark:bg-emerald-950/30">
                          <span className="italic text-2xl text-emerald-800 dark:text-emerald-200" style={{ fontFamily: 'cursive, "Brush Script MT"' }}>
                            {agreementData?.agent_signature_value || agreementData?.agent_signer_name}
                          </span>
                        </div>
                      )}
                      {agreementData?.agent_signature_type === 'drawn' && agreementData?.agent_signature_value && (
                        <img src={agreementData.agent_signature_value} alt="Agent signature" className="mt-2 max-h-20 mx-auto" />
                      )}
                    </div>
                  ) : !isAdmin ? (
                    <div className="space-y-3" data-testid="agent-sign-block">
                      <p className="text-xs text-gray-500">
                        Sign below using your full legal name (typed) or by drawing your signature.
                      </p>
                      <SignaturePad value={agentSig} onChange={setAgentSig} defaultName={agentName} testIdPrefix="agent-sig" />
                      <Button
                        onClick={handleSubmitSignature}
                        disabled={submittingSign}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="submit-agent-signature"
                      >
                        {submittingSign ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSignature className="w-4 h-4 mr-2" />}
                        Sign Agreement
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic mt-2">Agent has not signed yet.</p>
                  )}
                </div>
              </div>

              {fullySigned && (
                <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-200 text-sm">
                    <CheckCircle className="w-5 h-5" />
                    <span><b>Agreement is fully signed.</b> Both parties received an email confirmation with the countersigned PDF.</span>
                  </div>
                  <Button onClick={downloadSignedPdf} variant="outline" size="sm" className="bg-white" data-testid="download-signed-pdf-btn">
                    <Download className="w-4 h-4 mr-1" />
                    Download signed PDF
                  </Button>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4 border-t">
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => {
                  const token = localStorage.getItem('token');
                  window.open(`${WATERMARKED_AGREEMENT_URL}?token=${token}`, '_blank');
                }}
              >
                <Download className="w-4 h-4" />
                Download Full PDF (Watermarked)
              </Button>
              
              {!readOnly && (
                <div className="flex gap-2">
                  {isEditing && (
                    <Button 
                      variant="outline" 
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button 
                    onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                    disabled={isSaving}
                    className={isEditing ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : isEditing ? (
                      <Save className="w-4 h-4 mr-1" />
                    ) : (
                      <Edit2 className="w-4 h-4 mr-1" />
                    )}
                    {isEditing ? 'Save Agreement' : 'Edit Details'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AgentAgreement;
